import React, { useContext, useState, KeyboardEvent, type JSX } from 'react'
import styled from 'styled-components'
import { space, SpaceProps, compose, color } from 'styled-system'
import { GridItem } from '@zuto/core/src/grid'
import Box from '@zuto/core/src/box'
import { ArrowLeft, ArrowRight } from '@zuto/icons'
import { Button, TermsAndConditions } from '@zuto/atoms'
import QuestionComponentProps from '../../Questions/question-component-props'
import { ApplicationFormContext, MarketingPreferencesType } from '../ApplicationForm/ApplicationFormContext'
import withQuestion from '../HOC/withQuestion'
import withFade from '../HOC/withFade'
import { questionFontStyle, getCTAText } from '../Questions/utils'
import {
  checkIsAnswersMissing, logPrefilledAnswersThatHaveBeenChangedAgain, logAddedQuestionKeys, logMarketingPreferences
} from '../../utils/PrefillReviewPageApplicationForm-utils'
import { PrefillReviewPageApplicationFormContext } from '../ApplicationForm/PrefillReviewPageApplicationFormContext'
import { RepRatesBySource } from '../../GlobalVariables/RepRates'
import { Checkbox, FormControlLabel, SvgIcon, Typography } from '@mui/material'
import CenteredButton from '../../style/CenteredCTA'

const StyledDiv = styled.div<SpaceProps>`
display: flex;
flex-wrap: wrap;
  ${space}
  `

const ThankYouMessage = styled(Box)`
a {
    text-decoration: none;
    ${(props) => compose(color)({
  ...props,
  color: 'secondary.1'
})}
  }
`

const StyledPreviousButton = styled(Button)`
  justify-content: center;
  text-align: center;
  svg {
    margin-right: 16px;
  }

`

const ThinCheckBoxIcon = (props: any) => (
  <SvgIcon {...props}>
    <rect
      x="3"
      y="3"
      width="18"
      height="18"
      rx="2"
      stroke="currentColor"
      strokeWidth="0.5"
      fill="none"
    />
  </SvgIcon>
)

const ThinCheckBoxCheckedIcon = (props: any) => (
  <SvgIcon {...props}>
    <rect
      x="3"
      y="3"
      width="18"
      height="18"
      rx="2"
      stroke="currentColor"
      strokeWidth="0.5"
      fill="currentColor"
    />
    <path
      d="M7 12l2.5 2.5L17 8"
      stroke="#fff"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SvgIcon>
)

const Thankyou = ({
  disablePrevious,
  prefillData,
  initialAnswerState,
  sourceDisplayName,
  attribution
}: QuestionComponentProps) => {
  const appFormContext = useContext(ApplicationFormContext)
  const { experimentName } = useContext(PrefillReviewPageApplicationFormContext)
  const [marketingPreferences, setMarketingPreferences] = useState<MarketingPreferencesType>(appFormContext.getAppForm().marketingPreferences)
  const { featureSwitches } = appFormContext
  const { isReapply, reviewPageV3PreApproval, marketingOptInV1 } = featureSwitches ?? {}

  const isDataMissing = checkIsAnswersMissing([prefillData])

  let repExampleText: JSX.Element | string = ''

  if (isReapply && attribution?.medium.toLowerCase() === 'sms') {
    repExampleText = (
      <>
        <br /><br />
        Zuto is a credit broker not a lender. Representative {RepRatesBySource.default.repAPR} APR.
      </>
    )
  }

  const ctaText = getCTAText(sourceDisplayName, isReapply)
  const message = "As part of our service to you we'll contact you about your application to keep you updated. We want you to be the first to know about offers and other relevant information from Zuto. This will be related to vehicle finance or your vehicle, with occasional information from our <a href='/privacy/#our-partners' target='_blank'>partners</a>. Let us know how you'd like us to contact you:"

  const previousQuestion = () => {
    appFormContext.dispatch({
      type: 'SET_MARKETING_PREFERENCES',
      value: marketingPreferences
    })
    appFormContext.dispatch({
      type: 'PREVIOUS_QUESTION'
    })
  }

  const submit = () => {
    logMarketingPreferences(marketingPreferences, experimentName)

    if (prefillData && initialAnswerState) {
      logPrefilledAnswersThatHaveBeenChangedAgain(initialAnswerState, prefillData, experimentName)
      logAddedQuestionKeys(initialAnswerState, prefillData, experimentName)
    }

    appFormContext.dispatch({
      type: 'SUBMIT',
      marketingPreferences: {
        ...marketingPreferences,
        consentText: message
      }
    })
  }

  const updateMarketingPreferences = (key: string, val: boolean) => {
    setMarketingPreferences((prev) => ({
      ...prev,
      [key]: val
    }))
  }

  const handleEnterKeyPress = (e: KeyboardEvent) => {
    if (e.code === 'Enter') {
      submit()
    }
  }

  return (
    <>
      <GridItem gridColumn={['auto / span 12', '3 / span 8']} mt={0}>
        <Box
          textStyle={questionFontStyle}
          mt={4}
          mb={2}
        >
          One last thing
        </Box>
        {marketingOptInV1 ? (
          <>
            <Typography variant="bodyMedium" component="span" sx={{ mb: 5 }}>
              As part of our service we&apos;ll contact you about your application to keep you updated. We&apos;ll also send you relevant information, offers and updates from Zuto. If you don&apos;t want to receive offers tick here
            </Typography>
            <FormControlLabel
              sx={{ marginLeft: -2, verticalAlign: "text-top" }}
              label=""
              onChange={() => {
                updateMarketingPreferences('email', !marketingPreferences.email)
                updateMarketingPreferences('sms', !marketingPreferences.sms)
              } }
              control={(
                <Checkbox
                  sx={{ paddingTop: 0, paddingBottom: 0 }}
                  icon={<ThinCheckBoxIcon sx={{ fontSize: 16 }} />}
                  checkedIcon={<ThinCheckBoxCheckedIcon sx={{ fontSize: 16 }} />}
                  checked={!(marketingPreferences.email && marketingPreferences.sms)}
                  id="tag_email-and-sms-checkbox"
                  data-testid="emailAndSMS-checkbox"
                  onKeyDown={handleEnterKeyPress} />
              )} />
            </>
          ) : (
          <><ThankYouMessage
            textStyle="body.smallPrint.regular"
            mb={5}
            dangerouslySetInnerHTML={{
              __html: message
            }} /><StyledDiv mb={5}>
              <Box textStyle="material.body.medium.light" mb={4} minWidth="100%">
                Contact me by
              </Box>
              <FormControlLabel
                label="Email"
                onChange={() => updateMarketingPreferences('email', !marketingPreferences.email)}
                sx={{ flexBasis: '25%', '& .MuiTypography-root': (theme) => theme.typography.labelXLarge }}
                slotProps={{
                  typography: {
                    sx: (theme) => theme.typography.labelXLarge
                  }
                }}
                control={(
                  <Checkbox
                    sx={{ paddingTop: 0, paddingBottom: 0 }}
                    icon={<ThinCheckBoxIcon sx={{ fontSize: 42 }} />}
                    checkedIcon={<ThinCheckBoxCheckedIcon sx={{ fontSize: 42 }} />}
                    checked={marketingPreferences.email}
                    id="tag_email-checkbox"
                    data-testid="email-checkbox"
                    onKeyDown={handleEnterKeyPress} />
                )} />
              <FormControlLabel
                label="SMS"
                onChange={() => updateMarketingPreferences('sms', !marketingPreferences.sms)}
                sx={{ flexBasis: '25%', '& .MuiTypography-root': (theme) => theme.typography.labelXLarge }}
                control={(
                  <Checkbox
                    sx={{ paddingTop: 0, paddingBottom: 0 }}
                    icon={<ThinCheckBoxIcon sx={{ fontSize: 42 }} />}
                    checkedIcon={<ThinCheckBoxCheckedIcon sx={{ fontSize: 42 }} />}
                    checked={marketingPreferences.sms}
                    id="tag_sms-checkbox"
                    data-testid="sms-checkbox"
                    onKeyDown={handleEnterKeyPress} />
                )} />
            </StyledDiv></>
        )}
        <TermsAndConditions mb={5} mt={5}>
          By completing this application you agree to our
          {' '}
          <a href="/terms-and-conditions/" target="_blank">terms and conditions</a>
          {' '}
          and
          {' '}
          <a href="/privacy/" target="_blank">privacy policy</a>
          .
          <br />
          <br />
          We won&apos;t charge you anything for our service but we&apos;ll
          receive a commission from the lender which varies based
          on the product or amount borrowed. This doesn&apos;t
          affect the amount you pay.
          {repExampleText}
        </TermsAndConditions>
      </GridItem>
      <GridItem gridColumn={['1 / span 12', '3 / span 8']} mt={0} mb={[5, 4]}>
        <CenteredButton
          name="submit-button"
          id="centered-submit-button"
          data-testid="continue-button-center"
          endSvgIcon={ArrowRight}
          onClick={submit}
          disabled={prefillData && isDataMissing}
          size="large"
          subTextLineOne={!reviewPageV3PreApproval && ctaText ? ctaText.subTextLineOne : ''}
          subText={!reviewPageV3PreApproval ? 'No impact on your credit score' : ''}
        >
          {ctaText ? ctaText.title : 'Send my application'}
        </CenteredButton>
      </GridItem>

      {!prefillData && !disablePrevious && (
        <GridItem gridColumn={['1 / span 12', '3 / span 8']}>
          <StyledPreviousButton
            variant="tertiary"
            icon={<ArrowLeft />}
            onClick={previousQuestion}
            data-testid="previous-button-center"
            subText="(You won't lose your information)"
            size="largeCenteredText"
          >
            Previous
          </StyledPreviousButton>
        </GridItem>
      )}
    </>
  )
}

export default withFade(withQuestion(Thankyou))
